import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ThemeContext } from '../contexts/AppContext';
import { OptimizedMatrixRain, OptimizedNeuralNetwork, OptimizedFloatingCode } from './OptimizedAnimations';
import { ModularCog } from '../assets/logos/ModularCog';


// --- Theme Toggle Component ---
const ThemeToggle = () => {
  const { isDarkMode, toggleTheme } = React.useContext(ThemeContext);

  return (
    <button
      onClick={toggleTheme}
      className={`relative p-2 rounded-lg transition-all duration-300 transform hover:scale-110 ${
        isDarkMode
          ? 'bg-white/10 hover:bg-white/20 text-white'
          : 'bg-black/10 hover:bg-black/20 text-black'
      }`}
      title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
    >
      {isDarkMode ? (
        // Sun icon for light mode
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ) : (
        // Moon icon for dark mode
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
        </svg>
      )}
    </button>
  );
};

// --- Advanced Animation Components ---

// Matrix Code Rain Effect
const MatrixRain = ({ className = "" }) => {
  const canvasRef = useRef(null);
  const { isDarkMode } = React.useContext(ThemeContext);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    let animationFrameId;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    const characters = '01アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン';
    const fontSize = 14;
    const columns = canvas.width / fontSize;
    const drops = [];

    for (let i = 0; i < columns; i++) {
      drops[i] = 1;
    }

    const draw = () => {
      ctx.fillStyle = isDarkMode ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.05)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.fillStyle = isDarkMode ? '#fff' : '#000';
      ctx.font = `${fontSize}px 'Courier New', monospace`;

      for (let i = 0; i < drops.length; i++) {
        const text = characters.charAt(Math.floor(Math.random() * characters.length));
        ctx.fillText(text, i * fontSize, drops[i] * fontSize);

        if (drops[i] * fontSize > canvas.height && Math.random() > 0.975) {
          drops[i] = 0;
        }
        drops[i]++;
      }
      animationFrameId = requestAnimationFrame(draw);
    };

    resizeCanvas();
    draw();

    window.addEventListener('resize', resizeCanvas);

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, [isDarkMode]);

  return (
    <canvas 
      ref={canvasRef} 
      className={`absolute inset-0 w-full h-full ${className}`}
      style={{ opacity: 0.1 }}
    />
  );
};

// Advanced Neural Network Visualization
const NeuralNetwork = ({ className = "" }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let isActive = true;

    const resizeCanvas = () => {
      if (!canvas.offsetWidth || !canvas.offsetHeight) return;
      canvas.width = canvas.offsetWidth * 2;
      canvas.height = canvas.offsetHeight * 2;
      ctx.scale(2, 2);
    };

    class Node {
      constructor(x, y, layer) {
        this.x = x;
        this.y = y;
        this.layer = layer;
        this.connections = [];
        this.value = Math.random();
        this.targetValue = Math.random();
      }

      update() {
        this.value += (this.targetValue - this.value) * 0.1;
        if (Math.abs(this.value - this.targetValue) < 0.01) {
          this.targetValue = Math.random();
        }
      }

      draw() {
        const intensity = this.value * 255;
        ctx.beginPath();
        ctx.arc(this.x, this.y, 6, 0, Math.PI * 2);
        ctx.fillStyle = `rgb(${intensity}, ${intensity}, ${intensity})`;
        ctx.fill();
        ctx.strokeStyle = `rgba(255, 255, 255, ${this.value * 0.5})`;
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }
    }

    class Connection {
      constructor(from, to) {
        this.from = from;
        this.to = to;
        this.weight = Math.random() * 2 - 1;
      }

      draw() {
        if (!this.from || !this.to) return;
        const intensity = Math.abs(this.weight) * 255;
        ctx.beginPath();
        ctx.moveTo(this.from.x, this.from.y);
        ctx.lineTo(this.to.x, this.to.y);
        ctx.strokeStyle = `rgba(${intensity}, ${intensity}, ${intensity}, ${Math.abs(this.weight) * 0.3})`;
        ctx.lineWidth = Math.abs(this.weight) * 2;
        ctx.stroke();
      }
    }

    const layers = [4, 6, 4, 3];
    const nodes = [];
    const connections = [];

    const createNetwork = () => {
      nodes.length = 0;
      connections.length = 0;

      if (canvas.width <= 0 || canvas.height <= 0) return;

      const layerWidth = canvas.width / 4;
      const maxNodes = Math.max(...layers);

      layers.forEach((layerSize, layerIndex) => {
        const x = layerWidth * (layerIndex + 0.5);
        const nodeSpacing = canvas.height / (maxNodes + 1);

        for (let i = 0; i < layerSize; i++) {
          const y = (i + 1) * nodeSpacing;
          const node = new Node(x, y, layerIndex);
          nodes.push(node);

          if (layerIndex > 0) {
            const prevLayerStart = layers.slice(0, layerIndex).reduce((sum, size) => sum + size, 0);
            const prevLayerSize = layers[layerIndex - 1];

            for (let j = 0; j < prevLayerSize; j++) {
              const fromNode = nodes[prevLayerStart + j];
              connections.push(new Connection(fromNode, node));
            }
          }
        }
      });
    };

    const animate = () => {
      if (!isActive) return;
      
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (nodes.length > 0) {
        nodes.forEach(node => node.update());
        connections.forEach(conn => conn.draw());
        nodes.forEach(node => node.draw());
      }

      animationFrameId = requestAnimationFrame(animate);
    };

    resizeCanvas();
    createNetwork();
    animate();

    window.addEventListener('resize', () => {
      resizeCanvas();
      createNetwork();
    });

    return () => {
      isActive = false;
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);

  return (
    <canvas 
      ref={canvasRef} 
      className={`absolute inset-0 w-full h-full ${className}`}
    />
  );
};

// Floating Code Elements
const FloatingCode = () => {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    
    const elements = container.children;

    const animate = () => {
      Array.from(elements).forEach((el, index) => {
        const time = Date.now() * 0.001;
        const delay = index * 0.5;
        const y = Math.sin(time + delay) * 10;
        const x = Math.cos(time * 0.5 + delay) * 8;
        const rotation = Math.sin(time * 0.3 + delay) * 3;
        
        el.style.transform = `translate(${x}px, ${y}px) rotate(${rotation}deg)`;
      });
      requestAnimationFrame(animate);
    };

    animate();
  }, []);

  return (
    <div ref={containerRef} className="absolute inset-0 pointer-events-none">
      <div className="absolute top-16 left-16 text-cyan-400 font-mono text-xs opacity-60">consciousness.emergence()</div>
      <div className="absolute top-32 right-24 text-purple-400 font-mono text-xs opacity-50">synthesis.quantum()</div>
      <div className="absolute bottom-24 left-24 text-green-400 font-mono text-xs opacity-55">intelligence.scale()</div>
      <div className="absolute bottom-16 right-16 text-blue-400 font-mono text-xs opacity-45">reasoning.abstract()</div>
      <div className="absolute top-48 left-1/3 text-yellow-400 font-mono text-xs opacity-40">understanding.deep()</div>
      <div className="absolute bottom-32 right-1/3 text-pink-400 font-mono text-xs opacity-35">creativity.generative()</div>
      <div className="absolute top-1/3 right-16 text-orange-400 font-mono text-xs opacity-50">learning.continuous()</div>
      <div className="absolute bottom-1/3 left-8 text-indigo-400 font-mono text-xs opacity-45">perception.multimodal()</div>
    </div>
  );
};

// --- Layout Components ---

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLeftMenuOpen, setIsLeftMenuOpen] = useState(false);
  const [activeSubMenu, setActiveSubMenu] = useState(null);
  const { isDarkMode } = React.useContext(ThemeContext);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const headerBgClass = isScrolled
    ? isDarkMode
      ? 'bg-black/90 backdrop-blur-xl border-b border-white/10'
      : 'bg-white/90 backdrop-blur-xl border-b border-black/10'
    : 'bg-transparent';

  const textColorClass = isDarkMode ? 'text-white' : 'text-black';
  const hoverTextColorClass = isDarkMode ? 'hover:text-white' : 'hover:text-black';
  const mutedTextColorClass = isDarkMode ? 'text-gray-300' : 'text-gray-700';
  const borderColorClass = isDarkMode ? 'border-white/20' : 'border-black/20';
  const bgColorClass = isDarkMode ? 'bg-white/10' : 'bg-black/10';
  const hoverBgColorClass = isDarkMode ? 'hover:bg-white/20' : 'hover:bg-black/20';
  const shadowColorClass = isDarkMode ? 'hover:shadow-white/20' : 'hover:shadow-black/20';

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${headerBgClass}`}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex-shrink-0">
            <Link to="/" className={`text-lg font-mono font-bold tracking-wider relative group ${textColorClass}`}>
              <ModularCog isScrolled={isScrolled} className="transition-all duration-500" />
              <div className={`absolute -bottom-1 left-0 w-0 h-0.5 ${isDarkMode ? 'bg-white' : 'bg-black'} transition-all duration-300 group-hover:w-full shadow-lg ${isDarkMode ? 'shadow-white/50' : 'shadow-black/50'}`}></div>
            </Link>
          </div>
          <nav className="hidden md:block">
            <div className="ml-8 flex items-baseline space-x-6">
              <Link to="/about" className={`${mutedTextColorClass} ${hoverTextColorClass} px-2 py-1 rounded text-sm font-mono transition-all duration-300 relative group`}>
                About
                <div className={`absolute -bottom-1 left-0 w-0 h-0.5 ${isDarkMode ? 'bg-white' : 'bg-black'} transition-all duration-300 group-hover:w-full`}></div>
              </Link>
              <Link to="/research" className={`${mutedTextColorClass} ${hoverTextColorClass} px-2 py-1 rounded text-sm font-mono transition-all duration-300 relative group`}>
                Research
                <div className={`absolute -bottom-1 left-0 w-0 h-0.5 ${isDarkMode ? 'bg-white' : 'bg-black'} transition-all duration-300 group-hover:w-full`}></div>
              </Link>
              <Link to="/engineering" className={`${mutedTextColorClass} ${hoverTextColorClass} px-2 py-1 rounded text-sm font-mono transition-all duration-300 relative group`}>
                Engineering
                <div className={`absolute -bottom-1 left-0 w-0 h-0.5 ${isDarkMode ? 'bg-white' : 'bg-black'} transition-all duration-300 group-hover:w-full`}></div>
              </Link>
              <Link to="/services" className={`${mutedTextColorClass} ${hoverTextColorClass} px-2 py-1 rounded text-sm font-mono transition-all duration-300 relative group`}>
                Services
                <div className={`absolute -bottom-1 left-0 w-0 h-0.5 ${isDarkMode ? 'bg-white' : 'bg-black'} transition-all duration-300 group-hover:w-full`}></div>
              </Link>
            </div>
          </nav>
          <div className="flex items-center space-x-4">
            <ThemeToggle />
            <div className="hidden md:block">
            <Link to="/demo" className={`relative ${bgColorClass} backdrop-blur-sm ${textColorClass} px-4 py-2 rounded-lg text-sm font-mono font-semibold transition-all duration-300 transform hover:scale-105 ${hoverBgColorClass} hover:shadow-2xl ${shadowColorClass} group overflow-hidden ${borderColorClass}`}>
              <span className="relative z-10">Demo</span>
              <div className={`absolute inset-0 ${bgColorClass} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
            </Link>
          </div>
            <button
              onClick={() => setIsLeftMenuOpen(!isLeftMenuOpen)}
              className={`md:hidden ${mutedTextColorClass} ${hoverTextColorClass} p-2`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Left Slide Menu */}
      <>
        {/* Overlay */}
        <div 
          className={`fixed top-16 left-0 right-0 bottom-0 z-40 transition-opacity duration-300 ${
            isLeftMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          } ${isDarkMode ? 'bg-black/50' : 'bg-white/50'} backdrop-blur-sm`}
          onClick={() => setIsLeftMenuOpen(false)}
        />
        
        {/* Menu Panel */}
        <div className={`fixed top-16 left-0 h-[calc(100vh-4rem)] w-72 z-50 transform transition-transform duration-300 ease-in-out ${
          isLeftMenuOpen ? 'translate-x-0' : '-translate-x-full'
        } ${isDarkMode ? 'bg-black/95' : 'bg-white/95'} backdrop-blur-xl border-r ${isDarkMode ? 'border-white/10' : 'border-black/10'} rounded-r-2xl`}>
          <div className="p-6">
            <div className="flex items-center justify-end mb-8">
              <button
                onClick={() => setIsLeftMenuOpen(false)}
                className={`${mutedTextColorClass} ${hoverTextColorClass} p-2`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <nav className="space-y-1">
              {/* Models (6 letters) */}
              <div>
                <button
                  onClick={() => setActiveSubMenu(activeSubMenu === 'models' ? null : 'models')}
                  className={`w-full text-left ${mutedTextColorClass} ${hoverTextColorClass} font-mono text-sm py-2 px-3`}
                >
                  Models
                </button>
                <div className={`ml-4 space-y-1 overflow-hidden transition-all duration-500 ease-out ${activeSubMenu === 'models' ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'}`}>
                  <Link to="/models/katana" onClick={() => setIsLeftMenuOpen(false)} className={`block ${mutedTextColorClass} ${hoverTextColorClass} font-mono text-xs py-1 px-3 transition-all duration-300 ease-out ${activeSubMenu === 'models' ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'}`} style={{transitionDelay: activeSubMenu === 'models' ? '100ms' : '0ms'}}>Katana</Link>
                  <Link to="/models/iceberg" onClick={() => setIsLeftMenuOpen(false)} className={`block ${mutedTextColorClass} ${hoverTextColorClass} font-mono text-xs py-1 px-3 transition-all duration-300 ease-out ${activeSubMenu === 'models' ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'}`} style={{transitionDelay: activeSubMenu === 'models' ? '200ms' : '0ms'}}>ICEBERG</Link>
                  <Link to="/models/thesidia" onClick={() => setIsLeftMenuOpen(false)} className={`block ${mutedTextColorClass} ${hoverTextColorClass} font-mono text-xs py-1 px-3 transition-all duration-300 ease-out ${activeSubMenu === 'models' ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'}`} style={{transitionDelay: activeSubMenu === 'models' ? '300ms' : '0ms'}}>Thesidia</Link>
                  <Link to="/models/ice-nano" onClick={() => setIsLeftMenuOpen(false)} className={`block ${mutedTextColorClass} ${hoverTextColorClass} font-mono text-xs py-1 px-3 transition-all duration-300 ease-out ${activeSubMenu === 'models' ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'}`} style={{transitionDelay: activeSubMenu === 'models' ? '400ms' : '0ms'}}>Ice-nano</Link>
                </div>
              </div>

              {/* Product (7 letters) */}
              <div>
                <button
                  onClick={() => setActiveSubMenu(activeSubMenu === 'product' ? null : 'product')}
                  className={`w-full text-left ${mutedTextColorClass} ${hoverTextColorClass} font-mono text-sm py-2 px-3`}
                >
                  Product
                </button>
                <div className={`ml-4 space-y-1 overflow-hidden transition-all duration-500 ease-out ${activeSubMenu === 'product' ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'}`}>
                  <Link to="/platform" onClick={() => setIsLeftMenuOpen(false)} className={`block ${mutedTextColorClass} ${hoverTextColorClass} font-mono text-xs py-1 px-3 transition-all duration-300 ease-out ${activeSubMenu === 'product' ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'}`} style={{transitionDelay: activeSubMenu === 'product' ? '100ms' : '0ms'}}>Platform</Link>
                  <Link to="/enterprise" onClick={() => setIsLeftMenuOpen(false)} className={`block ${mutedTextColorClass} ${hoverTextColorClass} font-mono text-xs py-1 px-3 transition-all duration-300 ease-out ${activeSubMenu === 'product' ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'}`} style={{transitionDelay: activeSubMenu === 'product' ? '200ms' : '0ms'}}>Enterprise</Link>
                  <Link to="/iceberg-overview" onClick={() => setIsLeftMenuOpen(false)} className={`block ${mutedTextColorClass} ${hoverTextColorClass} font-mono text-xs py-1 px-3 transition-all duration-300 ease-out ${activeSubMenu === 'product' ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'}`} style={{transitionDelay: activeSubMenu === 'product' ? '300ms' : '0ms'}}>Iceberg Overview</Link>
                  <Link to="/iceberg-protocol" onClick={() => setIsLeftMenuOpen(false)} className={`block ${mutedTextColorClass} ${hoverTextColorClass} font-mono text-xs py-1 px-3 transition-all duration-300 ease-out ${activeSubMenu === 'product' ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'}`} style={{transitionDelay: activeSubMenu === 'product' ? '400ms' : '0ms'}}>Iceberg Protocol</Link>
                </div>
              </div>

              {/* Research (8 letters) */}
              <div>
                <button
                  onClick={() => setActiveSubMenu(activeSubMenu === 'research' ? null : 'research')}
                  className={`w-full text-left ${mutedTextColorClass} ${hoverTextColorClass} font-mono text-sm py-2 px-3`}
                >
                  Research
                </button>
                <div className={`ml-4 space-y-1 overflow-hidden transition-all duration-500 ease-out ${activeSubMenu === 'research' ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'}`}>
                  <Link to="/research-index" onClick={() => setIsLeftMenuOpen(false)} className={`block ${mutedTextColorClass} ${hoverTextColorClass} font-mono text-xs py-1 px-3 transition-all duration-300 ease-out ${activeSubMenu === 'research' ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'}`} style={{transitionDelay: activeSubMenu === 'research' ? '100ms' : '0ms'}}>Index</Link>
                  <Link to="/research" onClick={() => setIsLeftMenuOpen(false)} className={`block ${mutedTextColorClass} ${hoverTextColorClass} font-mono text-xs py-1 px-3 transition-all duration-300 ease-out ${activeSubMenu === 'research' ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'}`} style={{transitionDelay: activeSubMenu === 'research' ? '200ms' : '0ms'}}>Research Overview</Link>
                  <Link to="/research/publications" onClick={() => setIsLeftMenuOpen(false)} className={`block ${mutedTextColorClass} ${hoverTextColorClass} font-mono text-xs py-1 px-3 transition-all duration-300 ease-out ${activeSubMenu === 'research' ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'}`} style={{transitionDelay: activeSubMenu === 'research' ? '300ms' : '0ms'}}>Publications</Link>
                  <Link to="/research/methodology" onClick={() => setIsLeftMenuOpen(false)} className={`block ${mutedTextColorClass} ${hoverTextColorClass} font-mono text-xs py-1 px-3 transition-all duration-300 ease-out ${activeSubMenu === 'research' ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'}`} style={{transitionDelay: activeSubMenu === 'research' ? '400ms' : '0ms'}}>Methodology</Link>
                </div>
              </div>

              {/* Solutions (9 letters) */}
              <div>
                <button
                  onClick={() => setActiveSubMenu(activeSubMenu === 'solutions' ? null : 'solutions')}
                  className={`w-full text-left ${mutedTextColorClass} ${hoverTextColorClass} font-mono text-sm py-2 px-3`}
                >
                  Solutions
                </button>
                <div className={`ml-4 space-y-1 overflow-hidden transition-all duration-500 ease-out ${activeSubMenu === 'solutions' ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'}`}>
                  <Link to="/solutions/education" onClick={() => setIsLeftMenuOpen(false)} className={`block ${mutedTextColorClass} ${hoverTextColorClass} font-mono text-xs py-1 px-3 transition-all duration-300 ease-out ${activeSubMenu === 'solutions' ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'}`} style={{transitionDelay: activeSubMenu === 'solutions' ? '100ms' : '0ms'}}>Education</Link>
                  <Link to="/small-business" onClick={() => setIsLeftMenuOpen(false)} className={`block ${mutedTextColorClass} ${hoverTextColorClass} font-mono text-xs py-1 px-3 transition-all duration-300 ease-out ${activeSubMenu === 'solutions' ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'}`} style={{transitionDelay: activeSubMenu === 'solutions' ? '150ms' : '0ms'}}>Small business</Link>
                  <Link to="/solutions/community" onClick={() => setIsLeftMenuOpen(false)} className={`block ${mutedTextColorClass} ${hoverTextColorClass} font-mono text-xs py-1 px-3 transition-all duration-300 ease-out ${activeSubMenu === 'solutions' ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'}`} style={{transitionDelay: activeSubMenu === 'solutions' ? '200ms' : '0ms'}}>Community</Link>
                  <Link to="/solutions/government" onClick={() => setIsLeftMenuOpen(false)} className={`block ${mutedTextColorClass} ${hoverTextColorClass} font-mono text-xs py-1 px-3 transition-all duration-300 ease-out ${activeSubMenu === 'solutions' ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'}`} style={{transitionDelay: activeSubMenu === 'solutions' ? '280ms' : '0ms'}}>Government</Link>
                  <Link to="/solutions/financial" onClick={() => setIsLeftMenuOpen(false)} className={`block ${mutedTextColorClass} ${hoverTextColorClass} font-mono text-xs py-1 px-3 transition-all duration-300 ease-out ${activeSubMenu === 'solutions' ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'}`} style={{transitionDelay: activeSubMenu === 'solutions' ? '360ms' : '0ms'}}>Financial Services</Link>
                </div>
              </div>

              {/* Direct Links - ordered by length */}
              <Link to="/demo" onClick={() => setIsLeftMenuOpen(false)} className={`block ${textColorClass} font-semibold font-mono text-sm py-2 px-3`}>Demo</Link>
              <Link to="/about" onClick={() => setIsLeftMenuOpen(false)} className={`block ${mutedTextColorClass} ${hoverTextColorClass} font-mono text-sm py-2 px-3`}>About</Link>
              <Link to="/services" onClick={() => setIsLeftMenuOpen(false)} className={`block ${mutedTextColorClass} ${hoverTextColorClass} font-mono text-sm py-2 px-3`}>Services</Link>
              <Link to="/engineering" onClick={() => setIsLeftMenuOpen(false)} className={`block ${mutedTextColorClass} ${hoverTextColorClass} font-mono text-sm py-2 px-3`}>Engineering</Link>
            </nav>
          </div>
        </div>
      </>
    </header>
  );
};

export const Hero = () => {
  const [textIndex, setTextIndex] = useState(0);
  const { isDarkMode } = React.useContext(ThemeContext);
  const heroTexts = [
    "Super",
    "Intelligence",
    "Research"
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setTextIndex((prev) => (prev + 1) % heroTexts.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [heroTexts.length]);

  const bgColorClass = isDarkMode ? 'bg-black' : 'bg-white';
  const textColorClass = isDarkMode ? 'text-white' : 'text-black';
  const mutedTextColorClass = isDarkMode ? 'text-gray-400' : 'text-gray-600';
  const borderColorClass = isDarkMode ? 'border-white/20' : 'border-black/20';
  const bgOpacityClass = isDarkMode ? 'bg-white/5' : 'bg-black/5';
  const buttonBgOpacityClass = isDarkMode ? 'bg-white/10' : 'bg-black/10';
  const buttonHoverBgOpacityClass = isDarkMode ? 'hover:bg-white/20' : 'hover:bg-black/20';
  const shadowColorClass = isDarkMode ? 'hover:shadow-white/20' : 'hover:shadow-black/20';
  const gridBgColor = isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)';

  return (
    <section id="home" className={`relative min-h-screen flex items-center justify-center text-center overflow-hidden ${bgColorClass} pt-16`}>
      <OptimizedMatrixRain />
      <OptimizedNeuralNetwork className="opacity-20" />
      <OptimizedFloatingCode />

      {/* Grid background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(${gridBgColor} 1px, transparent 1px),
                          linear-gradient(90deg, ${gridBgColor} 1px, transparent 1px)`,
          backgroundSize: '30px 30px'
        }}></div>
      </div>

      <div className="relative z-10 px-4 max-w-3xl mx-auto">
        <div className="mb-10">
          <h1 className={`text-3xl md:text-4xl lg:text-5xl font-mono font-bold ${textColorClass} tracking-tight leading-tight mb-6`}>
            Advanced
            <br />
            <span className={`${textColorClass} animate-pulse`}>
              {heroTexts[textIndex]}
            </span>
          </h1>
        </div>

        <div className={`mb-10 space-y-3 max-w-2xl mx-auto text-sm md:text-base ${mutedTextColorClass} leading-relaxed font-mono text-left sm:text-center`}>
          <p>
            Electrical-engineering-grade software company: hardware interfaces, custom applications, and integration
            layers—specs, tests, and ownership you can audit.
          </p>
          <p>
            We ship for operators and technical leaders who outgrew no-code dashboards: POS and retail systems, marketing
            and growth software, web surfaces from brochure to product, firmware-adjacent tooling, and privacy-first custom
            AI when failure is expensive.
          </p>
        </div>

        <div className="w-full max-w-full overflow-hidden">
          <div className="flex flex-nowrap sm:flex-wrap items-center justify-center gap-2 sm:gap-3">
          <Link to="/#features" className={`group relative ${buttonBgOpacityClass} backdrop-blur-sm ${textColorClass} px-3 sm:px-6 py-2 sm:py-3 rounded-lg text-[11px] sm:text-sm font-mono font-semibold text-center whitespace-nowrap transition-all duration-300 transform hover:scale-105 ${buttonHoverBgOpacityClass} hover:shadow-2xl ${shadowColorClass} overflow-hidden ${borderColorClass}`}>
            <span className="relative z-10">Explore Features</span>
            <div className={`absolute inset-0 ${buttonBgOpacityClass} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
          </Link>
          <Link to="/research" className={`group relative bg-transparent border ${borderColorClass} ${textColorClass} px-3 sm:px-6 py-2 sm:py-3 rounded-lg text-[11px] sm:text-sm font-mono font-semibold text-center whitespace-nowrap transition-all duration-300 transform hover:scale-105 hover:border-opacity-60 hover:bg-opacity-5 overflow-hidden`}>
            <span className="relative z-10">View Research</span>
            <div className={`absolute inset-0 ${bgOpacityClass} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
          </Link>
          <Link
            to="/small-business"
            className={`group relative px-3 sm:px-6 py-2 sm:py-3 rounded-lg text-[11px] sm:text-sm font-mono font-semibold text-center whitespace-nowrap transition-all duration-300 transform hover:scale-105 border-2 overflow-hidden ${
              isDarkMode
                ? 'bg-[#ff7700] text-stone-950 border-[#ffb84d] hover:bg-[#ff9830] shadow-[0_2px_0_rgba(0,0,0,0.3)]'
                : 'bg-[#ff6600] text-white border-[#e65100] hover:bg-[#ff8533] shadow-[0_2px_0_rgba(180,52,0,0.35)]'
            }`}
          >
            <span className="relative z-10 sm:hidden">Small Biz</span>
            <span className="relative z-10 hidden sm:inline">For small business</span>
          </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export const Footer = () => {
  const { isDarkMode } = React.useContext(ThemeContext);
  const [openDropdown, setOpenDropdown] = React.useState(null);

  const bgColorClass = isDarkMode ? 'bg-black/50' : 'bg-white/50';
  const borderColorClass = isDarkMode ? 'border-white/10' : 'border-black/10';
  const textColorClass = isDarkMode ? 'text-white' : 'text-black';
  const linkColorClass = isDarkMode ? 'text-gray-300' : 'text-gray-600';
  const hoverLinkColorClass = isDarkMode ? 'hover:text-white' : 'hover:text-black';
  const copyrightColorClass = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const radialBgColor = isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)';
  const dropdownBgClass = isDarkMode ? 'bg-black/80' : 'bg-white/80';
  const dropdownBorderClass = isDarkMode ? 'border-white/20' : 'border-black/20';

  const footerSections = [
    {
      title: "Product",
      links: [
        { name: "Iceberg Overview", href: "/iceberg-overview" },
        { name: "Iceberg AGI Protocol", href: "/iceberg-protocol" },
        { name: "Platform", href: "/platform" },
        { name: "Enterprise", href: "/enterprise" }
      ]
    },
    {
      title: "Research",
      links: [
        { name: "Research Overview", href: "/research" },
        { name: "Index", href: "/research-index" },
        { name: "Publications", href: "/research/publications" },
        { name: "Methodology", href: "/research/methodology" }
      ]
    },
    {
      title: "Models",
      links: [
        { name: "Thesidia", href: "/models/thesidia" },
        { name: "ICEBERG", href: "/models/iceberg" },
        { name: "Katana", href: "/models/katana" },
        { name: "Ice-nano", href: "/models/ice-nano" }
      ]
    },
    {
      title: "Transparency",
      links: [
        { name: "Data Non-Collection", href: "/transparency/data" },
        { name: "Scaling Responsibly", href: "/transparency/scaling" },
        { name: "Safety and Security", href: "/transparency/safety" },
        { name: "Ethics Framework", href: "/transparency/ethics" }
      ]
    },
    {
      title: "Solutions",
      links: [
        { name: "Education", href: "/solutions/education" },
        { name: "Small business", href: "/small-business" },
        { name: "Financial Services", href: "/solutions/financial" },
        { name: "Government", href: "/solutions/government" },
        { name: "Community", href: "/solutions/community" }
      ]
    },
    {
      title: "Learn",
      links: [
        { name: "Praxis Labs", href: "/learn/labs" },
        { name: "Global Engineers Alliance", href: "/learn/alliance" },
        { name: "Partner Directory", href: "/learn/partners" },
        { name: "People's Stories", href: "/learn/stories" }
      ]
    },
    {
      title: "Explore",
      links: [
        { name: "About Us", href: "/about" },
        { name: "Careers", href: "/careers" },
        { name: "Events", href: "/events" },
        { name: "News", href: "/news" }
      ]
    },
    {
      title: "Terms & Policy",
      links: [
        { name: "Terms of Service", href: "/terms/integrity" },
        { name: "Privacy Policy", href: "/terms/privacy" }
      ]
    }
  ];

  return (
    <footer className={`${bgColorClass} backdrop-blur-xl border-t ${borderColorClass} relative overflow-hidden`}>
      {/* Animated background */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, ${radialBgColor} 0%, transparent 50%)`,
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Main Footer Content */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-8 mb-12">
          {footerSections.map((section, index) => (
            <div key={index} className="relative">
              <button
                onClick={() => setOpenDropdown(openDropdown === index ? null : index)}
                className={`w-full text-left ${textColorClass} hover:opacity-80 transition-all duration-300 group`}
              >
                <h3 className={`text-sm font-mono font-semibold tracking-wider uppercase flex items-center justify-between`}>
                  {section.title}
                  <span className={`transform transition-transform duration-300 ${openDropdown === index ? 'rotate-180' : 'rotate-0'} text-xs`}>
                    ▼
                  </span>
                </h3>
              </button>
              
              {/* Dropdown Content */}
              <div className={`overflow-hidden transition-all duration-500 ease-in-out ${
                openDropdown === index ? 'max-h-48 opacity-100 mt-4' : 'max-h-0 opacity-0 mt-0'
              }`}>
                <ul className="space-y-2">
                  {section.links.map((link, linkIndex) => (
                    <li key={linkIndex} className={`transform transition-all duration-300 ${
                      openDropdown === index 
                        ? 'translate-y-0 opacity-100' 
                        : 'translate-y-2 opacity-0'
                    }`} style={{ transitionDelay: `${linkIndex * 50}ms` }}>
                      <Link 
                        to={link.href} 
                        className={`${linkColorClass} ${hoverLinkColorClass} transition-colors duration-300 font-mono text-xs hover:underline block py-1`}
                      >
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Section */}
        <div className={`border-t ${borderColorClass} pt-8`}>
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex items-center space-x-6">
              <Link to="/" className={`text-xl font-mono font-bold ${textColorClass} tracking-wider`}>
                PRAXIS
              </Link>
              <div className="hidden md:flex space-x-6">
                <Link to="/about" className={`${linkColorClass} ${hoverLinkColorClass} transition-colors duration-300 font-mono text-sm`}>About</Link>
                <Link to="/research" className={`${linkColorClass} ${hoverLinkColorClass} transition-colors duration-300 font-mono text-sm`}>Research</Link>
                <Link to="/engineering" className={`${linkColorClass} ${hoverLinkColorClass} transition-colors duration-300 font-mono text-sm`}>Engineering</Link>
                <Link to="/services" className={`${linkColorClass} ${hoverLinkColorClass} transition-colors duration-300 font-mono text-sm`}>Services</Link>
              </div>
            </div>
            <p className={`text-xs ${copyrightColorClass} font-mono`}>
              &copy; {new Date().getFullYear()} Praxis Research & Development. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};
